// ==================== GOOGLE ADSENSE INTEGRATION ====================
// Loads the Google AdSense library once, only when a publisher/client ID is
// configured. Nothing loads (and no ads render) until REACT_APP_ADSENSE_CLIENT
// is set in the environment, so this is dormant and safe by default.
//
// Compliance notes:
//  - A privacy policy disclosing cookie/advertising use is required (see the
//    "Advertising & Cookies" section of the Privacy Policy page).
//  - Add your publisher ID to public/ads.txt before serving live ads.
//  - For EEA/UK visitors you must also run a Google-certified Consent
//    Management Platform (CMP). See the audit notes.

import { logger } from './logger';

let adsenseInitialized = false;
let adsenseClientId = null;

// The site's AdSense publisher ID. The loader script is also placed in
// public/index.html <head> (Google's recommended placement). An env var
// override is supported for staging/alternate accounts.
const DEFAULT_ADSENSE_CLIENT = 'ca-pub-7354216231062372';

// Resolve the AdSense client ID (env var override, else the default above).
export const getAdSenseClientId = () =>
  String(process.env.REACT_APP_ADSENSE_CLIENT || DEFAULT_ADSENSE_CLIENT || '').trim();

export const isAdSenseConfigured = () => {
  const id = getAdSenseClientId();
  return id.startsWith('ca-pub-');
};

// Inject the AdSense script tag exactly once.
export const initAdSense = (clientId = getAdSenseClientId()) => {
  if (adsenseInitialized) return true;
  const id = String(clientId || '').trim();
  if (!id.startsWith('ca-pub-')) {
    if (id) logger.warn(`AdSense client ID "${id}" is invalid — it must start with "ca-pub-"`);
    return false;
  }
  if (typeof document === 'undefined') return false;

  adsenseClientId = id;
  adsenseInitialized = true;

  // The loader script is normally already in index.html <head>. Only inject it
  // if it isn't present (e.g. a build without the static tag) to avoid loading twice.
  const existing = document.querySelector('script[src*="adsbygoogle.js"]');
  if (!existing) {
    const script = document.createElement('script');
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${id}`;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => logger.error('Failed to load Google AdSense script');
    document.head.appendChild(script);
  }

  logger.log('AdSense initialized with:', id);
  return true;
};

export const isAdSenseReady = () => adsenseInitialized;

// Ask AdSense to fill a rendered <ins class="adsbygoogle"> slot.
export const pushAd = () => {
  if (!adsenseInitialized) return;
  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({});
  } catch (err) {
    logger.error('AdSense push failed:', err);
  }
};

export const getAdSenseClient = () => adsenseClientId;

export default { initAdSense, isAdSenseConfigured, getAdSenseClientId, isAdSenseReady, pushAd };
